import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api } from '../lib/api';
import { Drawer } from './Drawer';
import { annotateTitles } from '../lib/titleAnnotations';

// FULL-CV LEFT DRAWER (owner: "there should be a way to see the full original cv. that gives familiarity").
// The evaluation surface talks ABOUT the CV without ever showing it, so the reader has to hold the document in their
// head while reading a critique of it. This is the document itself, one tap away, at BOTH L1 and L2.
//
// READ-ONLY BY CONSTRUCTION. No textarea, no Save, no PATCH — the firewall is untouched: this shows the user their own
// words and offers no path to write, compose, or template anything.
//
// DATA SOURCE: the EXISTING `GET /api/cvs/:cv_id/model` — the populated-only, blueprint-grouped render model that
// CvPane and CvWorkspace already fetch and the export already renders. No new server route was needed. Fetched
// LAZILY on first open and cached for the view's lifetime.

interface MBullet { id: string; text: string }
interface MEntry { id: string; archetype: 'experience' | 'credential'; title: string | null; organization: string | null; dateText: string; bullets: MBullet[] }
interface MSection { id: string; name: string; entries: MEntry[] }
interface Model { contact: { name: string; email: string; phone: string; city: string }; sections: MSection[] }

// The org-by-default heading rule, identical to the evaluation surface's (QualityReadView.labelOf): the organization
// is part of WHAT THE ROLE IS, so it renders whenever the entry has one — dropped only when blank or when it IS the
// title (an untitled entry already fell back to its org upstream, and "X — X" reads as a bug).
export function entryLabel(e: { title: string | null; organization: string | null }): string {
  const title = (e.title ?? '').trim();
  const org = (e.organization ?? '').trim();
  if (!title) return org;
  return org.length > 0 && org !== title ? `${title} — ${org}` : title;
}

const SHEET_WIDTH = 700; // <700px → full-width sheet (the same breakpoint L2's columns stack at)

export function CvFullDrawer({ cvId, open, onOpen, onClose, focusSection }: {
  cvId: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  // The section currently under review at L2 (null at L1). Drives the ratified auto-scroll enhancement.
  focusSection?: string | null;
}) {
  const [model, setModel] = useState<Model | null>(null);
  const [failed, setFailed] = useState(false);
  const fetched = useRef(false);   // LAZY + CACHED: one request for the view's lifetime, on first open only
  // Remembered scroll offsets, keyed by the level the drawer was opened from. Preserved across open/close for the
  // session; the auto-scroll-to-section only fires the FIRST time a given L2 section opens the drawer, so scrolling
  // around inside the drawer is never undone by a reopen.
  const scrollByKey = useRef<Map<string, number>>(new Map());
  const scrollKey = focusSection ?? '__overview__';
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || fetched.current || !cvId) return;
    fetched.current = true;
    void (async () => {
      const r = await api<{ model: Model }>(`/api/cvs/${cvId}/model`);
      if (r.ok && r.data?.model) { setModel(r.data.model); setFailed(false); }
      else setFailed(true);
    })();
  }, [open, cvId]);

  // Restore the remembered offset — or, first time from an L2 section, scroll that section into view.
  const attachBody = useCallback((el: HTMLDivElement | null) => {
    bodyRef.current = el;
    if (!el) return;
    const remembered = scrollByKey.current.get(scrollKey);
    if (typeof remembered === 'number') { el.scrollTop = remembered; return; }
    if (!focusSection) return;
    const target = el.querySelector<HTMLElement>(`[data-cv-section="${cssEscape(focusSection)}"]`);
    if (target) el.scrollTop = Math.max(0, target.offsetTop - el.offsetTop);
  }, [scrollKey, focusSection]);

  // The content arrives after the panel mounts (lazy fetch), so re-run the placement once it does.
  useEffect(() => { if (open && model) attachBody(bodyRef.current); }, [open, model, attachBody]);

  const remember = () => { const el = bodyRef.current; if (el) scrollByKey.current.set(scrollKey, el.scrollTop); };
  const close = () => { remember(); onClose(); };

  const isSheet = useIsNarrow(SHEET_WIDTH);

  // IMG title-annotation DEDUP (owner + Sol ratified, 2026-08-08) — computed over the DRAWER's own
  // entries in document order (all sections, top to bottom), independently of the L2 surface. A rule's
  // full note renders on its first matching entry only (with the generic "applies below as well"
  // suffix when the rule matches again); later same-rule entries render nothing. Observership repeats
  // on every match. Recomputes when the model changes.
  const titleNotes = useMemo(() => {
    const entries = (model?.sections ?? []).flatMap((sec) => sec.entries);
    const notes = annotateTitles(entries.map((e) => e.title));
    return new Map(entries.map((e, i) => [e.id, notes[i]] as const));
  }, [model]);

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        style={S.fab}
        onClick={onOpen}
        data-testid="cv-drawer-open"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Your CV
      </button>
      <Drawer
        open={open}
        onClose={close}
        label="Your CV"
        testId="cv-drawer"
        side="left"
        width={isSheet ? '100vw' : 'min(92vw, 520px)'}
      >
        <div ref={attachBody} onScroll={remember} style={S.body} data-testid="cv-drawer-body">
          <p style={S.readOnlyNote} data-testid="cv-drawer-note">Your CV as it stands — read-only, in your own words.</p>
          {!model && !failed && <p style={S.muted} data-testid="cv-drawer-loading">Loading your CV…</p>}
          {failed && <p style={S.muted} data-testid="cv-drawer-failed">We couldn’t load your CV just now. Close this and try again in a moment.</p>}
          {model && model.sections.map((sec) => (
            <section key={sec.id} data-cv-section={sec.name} data-testid={`cv-drawer-section-${sec.name}`} style={S.section}>
              <h3 style={S.sectionName}>{sec.name}</h3>
              {sec.entries.map((e) => (
                <div key={e.id} style={S.entry} data-testid={`cv-drawer-entry-${e.id}`}>
                  <p style={S.entryTitle}>{entryLabel(e)}</p>
                  {/* IMG title annotation — same deterministic allowlist as the L2 surface (lib/titleAnnotations),
                      matched against the entry TITLE only (never the org). Drawer-prefixed testid: the drawer and
                      the L2 review mount together, and duplicated testids would collide. */}
                  {titleNotes.get(e.id) && (
                    <p style={S.titleAnnotation} data-testid={`cv-drawer-title-annotation-${e.id}`}>{titleNotes.get(e.id)}</p>
                  )}
                  {e.dateText && <p style={S.entryDate}>{e.dateText}</p>}
                  {e.bullets.length > 0 && (
                    <ul style={S.bullets}>
                      {e.bullets.map((b) => <li key={b.id} style={S.bullet}>{b.text}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      </Drawer>
    </>
  );
}

// CSS.escape isn't in every jsdom build; the section names are plain labels, so quote-escaping is enough.
function cssEscape(s: string): string { return s.replace(/["\\]/g, '\\$&'); }

// Viewport hook — mirrors QualityReadView/CvWorkspace/App's useIsNarrow (matchMedia guarded for jsdom).
function useIsNarrow(maxWidth: number): boolean {
  const mqOf = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`)
      : null;
  const [narrow, setNarrow] = useState(() => mqOf()?.matches ?? false);
  useEffect(() => {
    const mq = mqOf();
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxWidth]);
  return narrow;
}

const S: Record<string, CSSProperties> = {
  // Quiet, persistent, bottom-left — discoverable without competing with the review's own affordances.
  fab: {
    position: 'fixed', left: 'calc(env(safe-area-inset-left) + 14px)', bottom: 'calc(env(safe-area-inset-bottom) + 14px)',
    zIndex: 30, minHeight: 40, padding: 'var(--space-2) var(--space-4)', borderRadius: 999,
    // Owner ruling 2026-08-04: deep blue like the theme — the ink navy, light text.
    border: '1px solid var(--ink)', background: 'var(--ink, #102A43)', color: 'var(--canvas, #FAFAF7)',
    boxShadow: 'var(--elev-overlay)', fontSize: 'var(--fs-small)', cursor: 'pointer',
  },
  body: { overflowY: 'auto', maxHeight: 'calc(100vh - 110px)', paddingRight: 4 },
  readOnlyNote: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-label)', color: 'var(--muted, #777)' },
  muted: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)' },
  section: { margin: '0 0 var(--space-4)' },
  sectionName: { margin: '0 0 var(--space-2)', fontFamily: 'var(--serif)', fontSize: 15, color: 'var(--ink)', borderBottom: '1px solid var(--rule)', paddingBottom: 4 },
  entry: { margin: '0 0 var(--space-3)' },
  entryTitle: { margin: '0 0 2px', fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--ink)' },
  entryDate: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-label)', color: 'var(--muted, #777)' },
  titleAnnotation: { margin: '0 0 2px', fontSize: 'var(--fs-label)', fontStyle: 'italic', color: 'var(--muted, #777)', lineHeight: 'var(--lh-body)' },
  bullets: { margin: 0, paddingLeft: 18, fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', lineHeight: 'var(--lh-body)' },
  bullet: { margin: '0 0 var(--space-1)', whiteSpace: 'pre-wrap' },
};
